import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import { Pill, Search, Plus, Download, Check, FileText, Calendar, Send, Copy, ExternalLink, ShieldCheck } from 'lucide-react';
import { medicalRecordsApi, memedApi } from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { MeMedStatusBadge } from '@/components/memed/MeMedStatusBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';

interface Medication {
  name: string;
  dosage: string;
  quantity: string;
  frequency: string;
  duration: string;
  instructions: string;
}

interface Prescription {
  id: string;
  patientId: string;
  patientName: string;
  appointmentId: string;
  diagnosis: string;
  medications: Medication[];
  validityDays: number;
  status: 'DRAFT' | 'SIGNED' | 'ISSUED' | 'SENT' | 'CANCELLED';
  createdAt: string;
  signedAt?: string;
  crm: string;
  prescriptionNumber?: string;
  memedId?: string;
  memedPrescriptionId?: string;
  memedPdfUrl?: string;
  pdfUrl?: string;
  memedStatus?: 'PENDING' | 'SYNCED' | 'FAILED' | 'SIGNED' | 'CANCELLED';
}

interface PrescriptionsResponse {
  prescriptions: Prescription[];
  total: number;
  page: number;
  pageSize: number;
}

export function DoctorPrescriptionsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const doctorId = user?.id || '';
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'ALL' | 'DRAFT' | 'SIGNED' | 'ISSUED'>('ALL');
  const [selectedPrescription, setSelectedPrescription] = useState<Prescription | null>(null);
  const [copiedPinId, setCopiedPinId] = useState<string | null>(null);

  const sendToMemedMutation = useMutation({
    mutationFn: async (prescriptionId: string) => {
      await memedApi.sendToMemed(prescriptionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctor', 'prescriptions'] });
      queryClient.invalidateQueries({ queryKey: ['memed', 'prescription'] });
    },
  });

  const downloadPdfMutation = useMutation({
    mutationFn: async (prescription: Prescription) => {
      const targetUrl = prescription.memedPdfUrl || prescription.pdfUrl;
      if (targetUrl) {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      try {
        const result = await medicalRecordsApi.downloadPrescriptionPdf(prescription.id);
        const link = document.createElement('a');
        link.href = `data:${result.mimeType};base64,${result.pdf}`;
        link.download = `receita_${prescription.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err: any) {
        alert(err?.message || 'A receita médica ainda aguarda assinatura digital na MeMed.');
      }
    },
  });

  const { data, isLoading, isError, refetch } = useQuery<PrescriptionsResponse>({
    queryKey: ['doctor', 'prescriptions', page, filter],
    queryFn: async () => {
      const response = await medicalRecordsApi.getDoctorPrescriptions(doctorId, page);
      return {
        prescriptions: response.data as any,
        total: response.meta.total,
        page: response.meta.page,
        pageSize: response.meta.limit,
      };
    },
  } as any);

  const filteredPrescriptions = data?.prescriptions.filter((p) => {
    const pName = (p.patientName || '').toLowerCase();
    const pDiag = (p.diagnosis || '').toLowerCase();
    const sTerm = search.toLowerCase();
    const matchesSearch = pName.includes(sTerm) ||
                          pDiag.includes(sTerm) ||
                          (p.medications || []).some(m => (m?.name || '').toLowerCase().includes(sTerm));
    const matchesFilter = filter === 'ALL' || p.status === filter;
    return matchesSearch && matchesFilter;
  }) || [];

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Não informada';
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(dateStr));
    } catch {
      return 'Data inválida';
    }
  };

  const getStatusBadge = (status: Prescription['status']) => {
    switch (status) {
      case 'DRAFT': return { bg: 'bg-muted text-muted-foreground border border-border', label: 'Rascunho' };
      case 'SIGNED': return { bg: 'bg-primary/10 text-primary border border-primary/20', label: 'Assinado' };
      case 'ISSUED': return { bg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20', label: 'Emitido' };
      case 'SENT': return { bg: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20', label: 'Enviado' };
      case 'CANCELLED': return { bg: 'bg-destructive/10 text-destructive border border-destructive/20', label: 'Cancelado' };
      default: return { bg: 'bg-muted text-muted-foreground', label: status || 'Desconhecido' };
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Receitas Médicas</h1>
          <p className="text-sm text-muted-foreground">Consulte o histórico de prescrições emitidas e emita novas receitas</p>
        </div>
        <Button
          onClick={() => navigate('/medico/receitas/novo')}
          variant="primary"
          className="gap-2 rounded-xl"
        >
          <Plus className="h-4 w-4" />
          <span>Nova Receita</span>
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative flex-1 w-full max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por paciente, diagnóstico ou medicamento..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          />
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="w-full sm:w-auto px-4 py-2 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        >
          <option value="ALL">Todos os Status</option>
          <option value="DRAFT">Rascunho</option>
          <option value="SIGNED">Assinado</option>
          <option value="ISSUED">Emitido</option>
        </select>
      </div>

      {isLoading ? (
        <LoadingOverlay message="Carregando receitas..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar receitas" onRetry={() => refetch()} />
      ) : filteredPrescriptions.length === 0 ? (
        <EmptyState
          icon="Pill"
          title="Nenhuma receita encontrada"
          description={search || filter !== 'ALL' ? 'Tente ajustar os filtros' : 'Você ainda não criou nenhuma receita'}
          action={{ label: 'Criar Receita', onClick: () => navigate('/medico/receitas/novo') }}
        />
      ) : (
        <div className="space-y-4">
          {filteredPrescriptions.map((prescription) => {
            const statusBadge = getStatusBadge(prescription.status);
            const displayDate = prescription.createdAt || (prescription as any).issuedAt;
            return (
              <Card key={prescription.id} className="bg-card border-border">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl ${
                        prescription.status === 'ISSUED' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
                        prescription.status === 'SIGNED' ? 'bg-primary/10 text-primary' :
                        prescription.status === 'DRAFT' ? 'bg-muted text-muted-foreground' : 'bg-destructive/10 text-destructive'
                      }`}>
                        <Pill className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-lg text-foreground">{prescription.patientName}</h3>
                          {prescription.prescriptionNumber && (
                            <span className="px-2 py-0.5 bg-primary/10 text-primary border border-primary/20 text-xs rounded-full">
                              {prescription.prescriptionNumber}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{prescription.diagnosis || 'Sem diagnóstico informado'}</p>
                        {prescription.crm && <p className="text-xs text-muted-foreground mt-1">CRM: {prescription.crm}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 text-xs rounded-full font-semibold ${statusBadge.bg}`}>
                        {statusBadge.label}
                      </span>
                      {(prescription.memedStatus || prescription.memedId) && (
                        <MeMedStatusBadge
                          status={prescription.memedStatus || 'PENDING'}
                          memedId={prescription.memedId}
                        />
                      )}
                    </div>
                  </div>

                  {/* Painel da Receita Digital & PIN da Farmácia */}
                  {(() => {
                    const unlockCode =
                      (prescription as any).digitalPrescription?.unlockCode ||
                      (prescription as any).content?.digitalPrescription?.unlockCode ||
                      (prescription as any).unlockCode;
                    const digitalLink =
                      (prescription as any).digitalPrescription?.link ||
                      (prescription as any).content?.digitalPrescription?.link ||
                      (prescription as any).digitalPrescriptionLink;

                    if (!unlockCode && !digitalLink && prescription.memedStatus !== 'SIGNED') {
                      return null;
                    }

                    return (
                      <div className="bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-800/60 rounded-xl p-3.5 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                            <ShieldCheck className="h-5 w-5" />
                          </div>
                          <div>
                            <span className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider block">
                              Receita Digital MeMed (ICP-Brasil)
                            </span>
                            {unlockCode && (
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-muted-foreground">PIN Farmácia:</span>
                                <span className="font-mono font-black text-sm tracking-wider text-emerald-900 dark:text-emerald-100 select-all">
                                  {unlockCode}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(unlockCode);
                                    setCopiedPinId(prescription.id);
                                    setTimeout(() => setCopiedPinId(null), 2000);
                                  }}
                                  className="text-emerald-600 hover:text-emerald-700"
                                  title="Copiar PIN"
                                >
                                  {copiedPinId === prescription.id ? (
                                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                                  ) : (
                                    <Copy className="h-3.5 w-3.5" />
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {digitalLink && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(digitalLink, '_blank')}
                              className="h-8 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-100/50"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              <span>Abrir Link Digital</span>
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div className="bg-muted/40 dark:bg-muted/20 border border-border/80 rounded-xl p-4 mb-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Medicamentos</p>
                    <div className="space-y-3">
                      {(prescription.medications || []).map((med, idx) => (
                        <div key={idx} className="bg-card border border-border rounded-xl p-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-bold text-foreground text-sm">{med.name}</p>
                              <p className="text-xs text-muted-foreground">{med.dosage} - {med.quantity} unidades</p>
                            </div>
                            <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-md">
                              {med.frequency}
                            </span>
                          </div>
                          {med.duration && (
                            <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                              <span>Duração: {med.duration}</span>
                            </div>
                          )}
                          {med.instructions && (
                            <p className="mt-2 text-xs text-primary italic">{med.instructions}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-border/80">
                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      {prescription.validityDays ? (
                        <div className="flex items-center gap-1">
                          <FileText className="h-4 w-4 text-primary" />
                          <span>Validade: {prescription.validityDays} dias</span>
                        </div>
                      ) : null}
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>Criado em {formatDate(displayDate)}</span>
                      </div>
                      {prescription.signedAt && (
                        <div className="flex items-center gap-1">
                          <Check className="h-4 w-4 text-emerald-500" />
                          <span>Assinado em {formatDate(prescription.signedAt)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                      <button
                        onClick={() => setSelectedPrescription(prescription)}
                        className="p-2 text-primary hover:bg-primary/10 rounded-xl transition-colors"
                        title="Visualizar detalhes"
                      >
                        <FileText className="h-5 w-5" />
                      </button>
                      {!prescription.memedId && prescription.status !== 'CANCELLED' && (
                        <button
                          onClick={() => sendToMemedMutation.mutate(prescription.id)}
                          disabled={sendToMemedMutation.isPending}
                          className="p-2 text-purple-600 hover:bg-purple-500/10 rounded-xl disabled:opacity-50 transition-colors"
                          title="Enviar para MeMed"
                        >
                          <Send className="h-5 w-5" />
                        </button>
                      )}
                      <button
                        onClick={() => downloadPdfMutation.mutate(prescription)}
                        disabled={downloadPdfMutation.isPending}
                        className="p-2 text-emerald-600 hover:bg-emerald-500/10 rounded-xl disabled:opacity-50 transition-colors"
                        title="Baixar Receita Oficial (MeMed)"
                      >
                        <Download className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-center gap-2 pt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
          className="rounded-xl border-border"
        >
          Anterior
        </Button>
        <span className="px-3.5 py-1.5 text-xs font-bold bg-primary text-primary-foreground rounded-xl shadow-xs">{page}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page + 1)}
          disabled={page * 10 >= (data?.total || 0)}
          className="rounded-xl border-border"
        >
          Próxima
        </Button>
      </div>

      {/* Modal de Detalhes da Receita */}
      {selectedPrescription && (
        <Dialog open={!!selectedPrescription} onOpenChange={(open) => !open && setSelectedPrescription(null)}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-foreground">
                <Pill className="w-5 h-5 text-primary" />
                <span>Receita de {selectedPrescription.patientName}</span>
              </DialogTitle>
              <DialogDescription>
                Emitida em {formatDate(selectedPrescription.createdAt || (selectedPrescription as any).issuedAt)}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 my-2">
              <div className="p-3.5 bg-muted/40 rounded-xl border border-border text-sm">
                <p className="font-semibold text-foreground">Diagnóstico / Hipótese:</p>
                <p className="text-muted-foreground mt-0.5">{selectedPrescription.diagnosis || 'Sem diagnóstico informado'}</p>
              </div>

              <div>
                <p className="font-semibold text-sm text-foreground mb-2">Medicamentos prescritos:</p>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {(selectedPrescription.medications || []).map((med, i) => (
                    <div key={i} className="p-3 bg-card border border-border rounded-xl text-sm">
                      <div className="flex justify-between items-start">
                        <span className="font-bold text-foreground">{med.name}</span>
                        <span className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">{med.dosage}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Posologia: {med.frequency} · Duração: {med.duration || 'Conforme orientação'}
                      </p>
                      {med.instructions && (
                        <p className="text-xs text-primary italic mt-1">{med.instructions}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedPrescription(null)}
                className="rounded-xl"
              >
                Fechar
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => downloadPdfMutation.mutate(selectedPrescription)}
                disabled={downloadPdfMutation.isPending}
                className="gap-2 rounded-xl"
              >
                <Download className="w-4 h-4" />
                <span>Baixar PDF</span>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
